import React, { useState, useEffect } from 'react';
import api from '../api';

export default function Performance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => { loadPerformance(); }, [period]);

  const loadPerformance = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/performance?period=${period}`);
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  const scoreColor = (score) => {
    if (score === null) return '#6b7280';
    if (score >= 85) return '#10b981';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.pageTitle}>🏆 Team Performance</div>
          <div style={styles.pageSubtitle}>Computed from real task completion data only</div>
        </div>
        <div style={styles.controls}>
          <input
            type="month"
            style={styles.monthInput}
            value={period}
            onChange={e => setPeriod(e.target.value)}
          />
          <button
            style={styles.btnSecondary}
            onClick={() => setPeriod(new Date().toISOString().slice(0, 7))}
          >
            This Month
          </button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Computing performance...</div>
      ) : (
        <>
          {/* Leaderboard */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>🥇 Performance Leaderboard — {period}</div>
            <div style={styles.note}>
              Quality Score and Approval Rate are not shown — no quality-rating system or
              per-person submission tracking exists yet to back those numbers honestly.
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Rank</th>
                  <th style={styles.th}>Member</th>
                  <th style={styles.th}>Tasks Done</th>
                  <th style={styles.th}>On-Time %</th>
                  <th style={styles.th}>Avg Turnaround</th>
                  <th style={styles.th}>Overall Score</th>
                </tr>
              </thead>
              <tbody>
                {data?.team?.map((m, i) => {
                  const color = scoreColor(m.score);
                  return (
                    <tr key={m.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.rank}>{m.score !== null ? (medals[i] || i + 1) : '—'}</span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.memberRow}>
                          <div style={{...styles.avatar, background: m.color}}>{m.initials}</div>
                          <div>
                            <div style={styles.memberName}>{m.name}</div>
                            <div style={styles.memberRole}>{m.role}</div>
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.mono}>{m.done}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.mono}>
                          {m.on_time_pct !== null ? `${m.on_time_pct}%` : '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.mono}>
                          {m.avg_turnaround_days !== null ? `${m.avg_turnaround_days}d` : '—'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {m.score !== null ? (
                          <div style={styles.scoreRow}>
                            <div style={styles.progressBg}>
                              <div style={{...styles.progressFill, width: `${m.score}%`, background: color}} />
                            </div>
                            <span style={{...styles.scoreVal, color}}>{m.score}</span>
                          </div>
                        ) : (
                          <span style={styles.noData}>No completed tasks this period</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={styles.grid2}>
            {/* Phase Completion */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>📊 Task Completion by Phase</div>
              {data?.phase_completion?.length === 0 && (
                <div style={styles.empty}>No tasks recorded yet.</div>
              )}
              {data?.phase_completion?.map(p => (
                <div key={p.phase} style={styles.phaseRow}>
                  <div style={styles.phaseName}>{p.phase.replace(/Phase \d+ – /, '')}</div>
                  <div style={styles.phaseProgress}>
                    <div style={styles.progressBg}>
                      <div style={{...styles.progressFill, width: `${p.pct}%`, background: '#6c58f0'}} />
                    </div>
                    <span style={styles.phasePct}>{p.pct}%</span>
                  </div>
                  <div style={styles.phaseCount}>{p.done}/{p.total}</div>
                </div>
              ))}
            </div>

            {/* Turnaround by Phase */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>⏱️ Avg Turnaround by Phase</div>
              {data?.turnaround_by_phase?.length === 0 && (
                <div style={styles.empty}>No completed tasks with timing data yet.</div>
              )}
              {data?.turnaround_by_phase?.map(p => (
                <div key={p.phase} style={styles.phaseRow}>
                  <div style={styles.phaseName}>{p.phase.replace(/Phase \d+ – /, '')}</div>
                  <div style={styles.turnaroundVal}>{p.avg_days}d avg</div>
                  <div style={styles.phaseCount}>{p.task_count} tasks</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
  loading: { padding: '40px', textAlign: 'center', color: '#9ca3af' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  pageTitle: { color: '#fff', fontSize: '22px', fontWeight: '700' },
  pageSubtitle: { color: '#6b7280', fontSize: '13px', marginTop: '2px' },
  controls: { display: 'flex', gap: '8px' },
  monthInput: { padding: '8px 12px', background: '#1a1d27', border: '1px solid #2a2d3e', borderRadius: '8px', color: '#e5e7eb', fontSize: '13px' },
  btnSecondary: { padding: '8px 16px', background: 'transparent', border: '1px solid #2a2d3e', borderRadius: '8px', color: '#9ca3af', fontSize: '13px', cursor: 'pointer' },
  card: { background: '#1a1d27', border: '1px solid #2a2d3e', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  cardTitle: { color: '#fff', fontSize: '15px', fontWeight: '600', marginBottom: '8px' },
  note: { color: '#6b7280', fontSize: '12px', marginBottom: '16px', fontStyle: 'italic' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 14px', textAlign: 'left', color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', borderBottom: '1px solid #2a2d3e' },
  tr: { borderBottom: '1px solid #2a2d3e' },
  td: { padding: '12px 14px', verticalAlign: 'middle' },
  rank: { fontSize: '18px' },
  memberRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  memberName: { color: '#e5e7eb', fontSize: '13px', fontWeight: '500' },
  memberRole: { color: '#6b7280', fontSize: '11px', textTransform: 'capitalize' },
  mono: { color: '#e5e7eb', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600' },
  scoreRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  progressBg: { height: '6px', background: '#2a2d3e', borderRadius: '3px', width: '80px' },
  progressFill: { height: '100%', borderRadius: '3px' },
  scoreVal: { fontSize: '13px', fontWeight: '700', fontFamily: 'monospace' },
  noData: { color: '#6b7280', fontSize: '12px' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  phaseRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '1px solid #2a2d3e' },
  phaseName: { color: '#e5e7eb', fontSize: '13px', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  phaseProgress: { display: 'flex', alignItems: 'center', gap: '8px', width: '120px' },
  phasePct: { color: '#9ca3af', fontSize: '12px', width: '35px', textAlign: 'right' },
  phaseCount: { color: '#6b7280', fontSize: '12px', width: '50px', textAlign: 'right' },
  turnaroundVal: { color: '#6c58f0', fontSize: '13px', fontWeight: '700', width: '60px', textAlign: 'right' },
  empty: { color: '#6b7280', fontSize: '13px', padding: '20px 0', textAlign: 'center' },
};